























































































import Vue from 'vue';
import * as actions from '@/store/action-types';
import { IAnalyticsSnapshot } from '@/store/types';
import { TopLevelSpec } from 'vega-lite';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { genBarSpec } from '@/components/Vis/Vega/Specs/TimeBarSpec';
import { mapGetters } from 'vuex';
import { LOAD_STATE } from '@/store/shared';
import { formatMoney, formatNumber, formatPercent } from '@/helpers/jsUtils';
import BarLoader from '@/components/UI/Loader/BarLoader.vue';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';

interface DeltaStats {
  label: string;
  primary: string;
  sub: string;
  subColor: string;
}

interface Metric {
  label: string;
  value: string;
  type: 'percent' | 'money' | 'number';
  chartLabel: string;
  chartColor: string;
  better: 'up' | 'down';
}

const metrics: Metric[] = [
  // { label: 'MRR', value: 'mrr', type: 'money', chartLabel: 'Monthly Recurring Revenue', chartColor: '#3c5ccf', better: 'up' },
  // { label: 'ARPU', value: 'arpu', type: 'money', chartLabel: 'Average Revenue Per User', chartColor: '#0e7490', better: 'up' },
  { label: 'Churn Rate', value: 'churnRate', type: 'percent', chartLabel: 'Revenue Churn', chartColor: 'rgb(162, 28, 175)', better: 'down' },
  { label: 'Active Subscriptions', value: 'currentSubs', type: 'number', chartLabel: 'Active Subscriptions', chartColor: '#047857', better: 'up' },
  { label: 'New Subscriptions', value: 'newSubs', type: 'number', chartLabel: 'New Subscriptions', chartColor: '#3c5ccf', better: 'up' },
  { label: 'Lost Subscriptions', value: 'lostSubs', type: 'number', chartLabel: 'Lost Subscriptions', chartColor: '#D8DFF6', better: 'down' },
];

export default Vue.extend({
  name: 'MetricHistory',
  components: {
    VueVegaEmbed,
    BarLoader,
    HeaderLoader,
  },
  data() {
    return {
      selectedMetric: 'churnRate',
      metrics,
      initialLoading: true,
    };
  },
  async created() {
    if (!(this.monthlySnapshots && this.monthlySnapshots.length)) {
      await this.$store.dispatch(actions.FETCH_ANALYTICS_SNAPSHOTS);
    }
    this.initialLoading = false;
  },
  computed: {
    ...mapGetters(['monthsAgoMetric', 'monthsAgoMetricDelta', 'lastMonthLabel']),
    metric(): Metric {
      return metrics.find((d) => d.value === this.selectedMetric) || metrics[0];
    },
    loading(): boolean {
      return this.$store.state.analyticsSnapshotsLoadState !== LOAD_STATE.LOADED;
    },
    monthlySnapshots(): IAnalyticsSnapshot[] {
      return this.$store.state.analyticsSnapshots;
    },
    monthlyVegaSpec(): TopLevelSpec | null {
      if (this.monthlySnapshots && this.monthlySnapshots.length) {
        const numMonths = this.monthlySnapshots.length;
        const startMonth = numMonths - 12;
        const endMonth = numMonths - 1;
        const chartData = this.monthlySnapshots.slice(startMonth, endMonth).reduce((acc, item) => {
          acc.push({
            Metric: this.metric.chartLabel,
            // @ts-ignore
            [this.metric.label]: item[this.metric.value],
            Month: new Date((item.startDate.getTime() + item.endDate.getTime()) / 2),
          });
          return acc;
        }, [] as any[]);
        return genBarSpec({
          data: chartData,
          xField: 'Month',
          yField: this.metric.label,
          colorField: 'Metric',
          timeUnit: 'yearmonth',
          xLabels: true,
          yLabels: true,
          // xAxisTitle: 'Month',
          // yAxisTitle: 'Churn Rate',
          colorScale: {
            domain: [this.metric.chartLabel], //, 'Involuntary'],
            range: [this.metric.chartColor], //, '#491249'],
          },
        });
      }
      return null;
    },
    comparisons(): DeltaStats[] {
      if (this.loading) {
        return [];
      }
      const lastValue = this.monthsAgoMetric(this.metric.value, 1);
      const stats = [
        {
          label: this.metric.label,
          primary: lastValue ? this.formatMetricValue(lastValue) : '-',
          sub: this.lastMonthLabel,
          subColor: 'text-gray-500 text-sm',
        },
      ];
      const comparisons = [
        [1, 'One Month Ago'],
        [2, 'Two Months Ago'],
        [12, 'One Year Ago'],
      ] as [number, string][];
      comparisons.forEach(([diff, label]) => {
        const delta = this.monthsAgoMetricDelta(this.metric.value, 1, diff + 1) as number;
        const value = this.monthsAgoMetric(this.metric.value, diff + 1) as number | null;
        if (value && delta > 0) {
          stats.push({
            label,
            primary: this.formatMetricValue(value),
            sub: `+${formatPercent(delta)}`,
            subColor: this.metric.better === 'up' ? 'text-green-600 text-xl' : 'text-brand-red text-xl',
          });
        } else if (value && delta < 0) {
          stats.push({
            label,
            primary: this.formatMetricValue(value),
            sub: formatPercent(delta),
            subColor: this.metric.better === 'down' ? 'text-green-600 text-xl' : 'text-brand-red text-xl',
          });
        } else {
          stats.push({
            label,
            primary: '-',
            sub: '',
            subColor: 'text-gray-500 text-sm',
          });
        }
      });
      const show = stats.reduce((test, d) => d.primary !== '-' || test, false);
      if (!show) {
        return [];
      }
      return stats;
    },
  },
  methods: {
    selectMetric(metric: Metric) {
      this.selectedMetric = metric.value;
    },
    exportData(data: Record<string, any> | Array<any>, name: string, { csv, fields }: { csv?: boolean; fields?: string[] }): void {
      this.$store.dispatch(actions.EXPORT_DATA, { data, name, fields, csv });
    },
    formatMetricValue(value: number): string {
      if (this.metric.type === 'percent') {
        return formatPercent(value / 100);
      }
      if (this.metric.type === 'number') {
        return formatNumber(value);
      }
      return formatMoney(value);
    },
  },
});
